/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import * as React from "react";
import { IStackProps, Stack } from "@fluentui/react";

import { HuxSpinButton, HuxTextBox } from "@nuance/hux-components";

import { IFieldDefinitionPanelSpinProps, IFieldDefinitionPanelSpinState } from ".";
import { panelFooterProps } from "./FieldDefinitionPanelStackTokens";
import { FieldDefinitionPanelSpinCommands } from "./FieldDefinitionPanelSpinViewModel";
import { IPanelFooterProps } from "./IPanelFooterProps";
import FieldDefinitionPanel from "./FieldDefinitionPanel";

const fieldDefinitionPanelSpinCommands = new FieldDefinitionPanelSpinCommands();
const cmds = fieldDefinitionPanelSpinCommands.Cmds;

const FieldDefinitionPanelSpin = (props: IFieldDefinitionPanelSpinProps): JSX.Element => {
    const initialState: IFieldDefinitionPanelSpinState = {
        common: { ...props.common },
        data: { ...props.data },
        errors: {},
        touched: {},
        isValid: !props.common.isNew,
        isDirty: false
    };

    const [state, dispatch] = React.useReducer(
        fieldDefinitionPanelSpinCommands.Reducer,
        initialState
    );

    const onSaveNewFieldDefinition = () => {
        // save the thing...
        if (props.common.onSaveContent !== undefined) {
            props.common.onSaveContent(fieldDefinitionPanelSpinCommands.Save(state));
        }
    };

    const spinStackProps: IStackProps = {
        tokens: {
            childrenGap: "12",
            maxWidth: "150px"
        }
    };

    const footerProps: IPanelFooterProps = {
        onSave: onSaveNewFieldDefinition,
        onCancel: props.common.onClosePanel,
        isNew: state.common.isNew,
        isDirty: state.isDirty,
        isValid: state.isValid,
        panelFooterProps
    };

    return (
        <FieldDefinitionPanel
            title={props.common.panelTitle}
            isOpen={props.common.isOpen}
            onClosePanel={props.common.onClosePanel}
            footerProps={footerProps}
            settingName={state.data.settingName}
            onChangeSettingName={(name, value) => dispatch(cmds.onTextChanged({ name, value }))}
            settingNameErrorMessage={state.touched.settingName ? state.errors.settingName : ""}
            onBlurSettingName={name => dispatch(cmds.onBlur(name))}
            settingNameReadOnly={!props.common.isNew}
            titleValue={state.data.title}
            onChangeTitle={(name, value) => dispatch(cmds.onTextChanged({ name, value }))}
            titleErrorMessage={state.touched.title ? state.errors.title : ""}
            onBlurTitle={name => dispatch(cmds.onBlur(name))}
            tooltipText={state.data.tooltip}
            helperText={state.data.helper}
            onHelpTextChange={changeData => {
                dispatch(cmds.onHelpTextChange(changeData));
            }}
            accessLevel={state.data.accessLevels}
            onAccessLevelChange={accessLevel => dispatch(cmds.onAccessLevelChanged(accessLevel))}
            auditChanges={state.data.auditChanges}
            onAuditChangesChanged={(checked: boolean) => dispatch(cmds.onAuditChanged(checked))}
            controlType={state.data.definitionType}
        >
            <Stack {...spinStackProps}>
                <HuxSpinButton
                    label="Min value"
                    value={state.data.minValue.toString()}
                    onChange={(_, min) => dispatch(cmds.onMinValueChanged(min))}
                    onBlur={() => dispatch(cmds.onBlur("minValue"))}
                    errorMessage={state.touched.minValue ? state.errors.minValue : ""}
                />
                <HuxSpinButton
                    label="Max value"
                    value={state.data.maxValue.toString()}
                    onChange={(_, max) => dispatch(cmds.onMaxValueChanged(max))}
                    onBlur={() => dispatch(cmds.onBlur("maxValue"))}
                    errorMessage={state.touched.maxValue ? state.errors.maxValue : ""}
                />
                <HuxSpinButton
                    label="Default value"
                    step={state.data.step}
                    value={state.data.defaultValue.toString()}
                    onChange={(_, value) => dispatch(cmds.onDefaultValueChanged(value))}
                    onBlur={() => dispatch(cmds.onBlur("defaultValue"))}
                    errorMessage={state.touched.defaultValue ? state.errors.defaultValue : ""}
                />
                <HuxSpinButton
                    label="Step"
                    min={1}
                    value={state.data.step.toString()}
                    onChange={(_, step) => dispatch(cmds.onStepChanged(step))}
                />
                <HuxTextBox
                    label="Suffix (e.g. minutes)"
                    name="Suffix"
                    value={state.data.suffix}
                    onChange={(_, value) => dispatch(cmds.onSuffixChanged(value))}
                />
            </Stack>
        </FieldDefinitionPanel>
    );
};

const SpinPanel = FieldDefinitionPanelSpin;
export { SpinPanel };
